import { useEffect, useState, useRef, useMemo } from 'react';
import { formatDate, formatDateForDisplay } from '../utils/dateUtils';
import { fetchWithCorpId } from '../utils/api';
import { useAuth, ROLES } from '../utils/auth';
import useServerTable from '../utils/useServerTable';
import useColumnPreferences, { useHiddenFilterReset } from '../utils/useColumnPreferences';
import * as XLSX from 'xlsx';
import Drawer from './Drawer';
import TimesheetForm from './TimesheetForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import ColumnPicker from './ui/ColumnPicker';
import { ColumnCells, ColumnFilterRow, ColumnHeaderRow, StatusBadge } from './ui/TableBits';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';

// One key per filterable column (issue #144); text keys map to same-named
// query params on GET /timesheets (backend #131). `status` stays an array so
// the repeatable ?status=A&status=B format keeps working; the column control
// is a single-select over it.
const EMPTY_FILTER = { employeeName: '', clientName: '', notes: '', status: [] };

function TimesheetTable({ onView }) {
  // Employees only view timesheets here (they fill them in Timesheet Items);
  // reviewing and creating are employer/admin work and the backend rejects them anyway.
  const { hasAnyRole } = useAuth();
  const canManage = hasAnyRole([ROLES.SUPER, ROLES.EMPLOYER, ROLES.ADMIN]);
  const toast = useToast();
  const confirm = useConfirm();
  // Drawer state
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (ts) => setDrawer({ mode: 'edit', id: ts.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); reload(); };

  const {
    rows: timesheets, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload, fetchAll,
  } = useServerTable('/timesheets', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'periodEndDate', direction: 'desc' } });

  const [openActionMenu, setOpenActionMenu] = useState(null);
  const menuRefs = useRef({});
  // Accumulate the status filter's options from every page seen so far, so the
  // dropdown keeps offering a status even after it is filtered out of view.
  const [statusOptions, setStatusOptions] = useState([]);
  useEffect(() => {
    setStatusOptions(prev => {
      const set = new Set(prev);
      timesheets.forEach(ts => { if (ts?.status) set.add(ts.status); });
      return Array.from(set).sort();
    });
  }, [timesheets]);

  useEffect(() => {
    const close = (e) => {
      if (openActionMenu) {
        const ref = menuRefs.current[openActionMenu];
        if (ref && !ref.contains(e.target)) setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openActionMenu]);

  // The column control is a single-select; '' means no status param at all,
  // otherwise the value goes out in the repeatable format the backend expects.
  const handleStatusSelectChange = (e) => {
    const { value } = e.target;
    setFilter(p => ({ ...p, status: value ? [value] : [] }));
  };

  const handleStatusChange = async (timesheetId, newStatus, successMessage) => {
    try {
      const res = await fetchWithCorpId(`/timesheets/${timesheetId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      reload();
      // The invoice can be created but fail to email; the server says so — surface it.
      if (res?.message && /fail/i.test(res.message)) toast.error(res.message);
      else if (successMessage) toast.success(successMessage);
    } catch (e) { toast.error(e.message || 'Failed to update timesheet status'); }
  };

  const handleAction = async (type, timesheet) => {
    setOpenActionMenu(null);
    if (type === 'View') { onView(timesheet); return; }
    // Approving is what generates the invoice — the invoice is emailed
    // automatically for an auto-invoiced client.
    if (type === 'Accept') {
      const ok = await confirm({
        title: 'Approve timesheet',
        message: timesheet.clientInvoiced
          ? 'Approving will generate the invoice and email it to the client.'
          : 'Approving will generate the invoice.',
        confirmLabel: 'Approve',
      });
      if (!ok) return;
      await handleStatusChange(timesheet.id, 'APPROVED', 'Timesheet approved');
      return;
    }
    if (type === 'Reject') {
      const ok = await confirm({
        title: 'Reject timesheet',
        message: 'The employee will need to resubmit.',
        confirmLabel: 'Reject',
        danger: true,
      });
      if (!ok) return;
      await handleStatusChange(timesheet.id, 'REJECTED', 'Timesheet rejected');
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const rows = await fetchAll();
      const ws = XLSX.utils.json_to_sheet(rows.map(ts => ({
        'Timesheet No.': ts.sequence ?? '', 'Timesheet ID': ts.id, Employee: ts.employeeName, Client: ts.clientName,
        'Billing Code': ts.billingCode, 'Start Date': formatDateForDisplay(ts.periodStartDate),
        'End Date': formatDateForDisplay(ts.periodEndDate), 'Total Hours': ts.totalHours,
        Status: ts.status, Notes: ts.notes,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Timesheets');
      XLSX.writeFile(wb, 'timesheets.xlsx');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  // Column catalogue in default order (issue #148). Employee is locked because
  // it is the row's open link; Status is locked because it is what a reviewer
  // scans for, and it keeps its mid table position rather than being pulled to
  // the front. Actions is locked everywhere.
  const columnCatalogue = useMemo(() => [
    {
      key: 'employeeName', label: 'Employee', locked: true,
      filter: { name: 'employeeName' },
      cell: ts => (
        <td style={{ fontWeight: '500' }}>
          {/* the employee name opens the timesheet; the internal id lives
              only in the tooltip for support conversations */}
          <button type="button" className="btn-link table-link" onClick={() => openEdit(ts)} title={`Internal id: ${ts.id}`}>
            {ts.employeeName}
          </button>
        </td>
      ),
    },
    {
      key: 'clientName', label: 'Client',
      filter: { name: 'clientName' },
      cell: ts => <td style={{ color: 'var(--muted)' }}>{ts.clientName}</td>,
    },
    {
      key: 'periodStartDate', label: 'Start',
      cell: ts => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(ts.periodStartDate)}</td>,
    },
    {
      key: 'periodEndDate', label: 'End',
      cell: ts => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(ts.periodEndDate)}</td>,
    },
    {
      key: 'totalHours', label: 'Hours',
      cell: ts => <td style={{ fontWeight: '600', color: 'var(--ink)' }}>{ts.totalHours}</td>,
    },
    {
      key: 'status', label: 'Status', locked: true,
      filter: {
        name: 'status',
        options: statusOptions,
        value: f => f.status[0] || '',
        onChange: handleStatusSelectChange,
      },
      cell: ts => <td><StatusBadge status={ts.status} /></td>,
    },
    {
      key: 'notes', label: 'Notes',
      filter: { name: 'notes' },
      cell: ts => <td style={{ color: 'var(--muted)', fontSize: '13px', maxWidth: '200px' }}>{ts.notes}</td>,
    },
    {
      key: 'actions', label: 'Actions', locked: true, sortable: false,
      cell: ts => (
        <td style={{ position: 'relative' }} ref={el => menuRefs.current[ts.id] = el}>
          <button
            onClick={() => setOpenActionMenu(openActionMenu === ts.id ? null : ts.id)}
            style={{ background: 'none', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', color: 'var(--muted)', display: 'flex', alignItems: 'center' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
          </button>
          {openActionMenu === ts.id && (
            <div className="action-menu">
              <div className="action-menu-item" onClick={() => handleAction('View', ts)}>View</div>
              {canManage && ts.status === 'Submitted' && (<>
                <div className="action-menu-item" onClick={() => handleAction('Accept', ts)}>
                  {ts.clientInvoiced ? 'Approve & invoice' : 'Approve'}
                </div>
                <div className="action-menu-item" onClick={() => handleAction('Reject', ts)}>Reject</div>
              </>)}
            </div>
          )}
        </td>
      ),
    },
    // the open menu, the status list and the reviewer flag all change what a
    // cell renders
  ], [openActionMenu, statusOptions, canManage]);

  const {
    columns, pickerColumns, toggle, move, step, reset, isDefault, saveError,
  } = useColumnPreferences('timesheets', columnCatalogue);

  useEffect(() => { if (saveError) toast.error(saveError); }, [saveError, toast]);
  useHiddenFilterReset(columns, setFilter, EMPTY_FILTER);

  return (
    <div className="page-container">
      <PageHeader
        title="Timesheets"
        subtitle="Track and manage employee timesheets"
        actions={
          <>
            <button className="btn-info" onClick={handleDownloadExcel}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
            {canManage && (
              <button className="btn-primary" onClick={openCreate}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Timesheet
              </button>
            )}
          </>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
        <ColumnPicker
          columns={pickerColumns}
          onToggle={toggle}
          onMove={move}
          onStep={step}
          onReset={reset}
          isDefault={isDefault}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <ColumnHeaderRow columns={columns} sortConfig={sortConfig} onSort={handleSort} />
            <ColumnFilterRow columns={columns} filter={filter} onChange={handleFilterChange} />
          </thead>
          <tbody style={{ opacity: loading && timesheets.length > 0 ? 0.55 : 1 }}>
            {timesheets.length === 0 ? (
              <TableState
                colSpan={columns.length}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No timesheets yet"
                emptyHint="Timesheets appear here once employees submit hours."
                emptyAction={canManage ? { label: 'New Timesheet', onClick: openCreate } : undefined}
              />
            ) : timesheets.map(ts => (
              <tr key={ts.id}>
                <ColumnCells columns={columns} row={ts} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit timesheet' : 'New timesheet'}
        subtitle={drawer?.mode === 'edit' ? 'Update period, hours, and status.' : 'Record a new timesheet for an employee.'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              {drawer?.mode === 'edit' ? 'Save changes' : 'Create timesheet'}
            </button>
          </>
        }
      >
        {drawer && (
          <TimesheetForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            timesheetId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default TimesheetTable;
